import { Platform } from 'react-native';
import { ConferenceTypeEnum, DeviceProfile } from '@matap/shared';
import * as Device from 'expo-device';
import { PlatformOSType } from 'react-native/Libraries/Utilities/Platform';
import { P2PPeerOptions } from '@/webrtc/p2p.peer';
import { registerWebrtcPolyfills } from '../../utils/polyfills';

export function setMediaBitrate(sdp: string, media: 'video' | 'audio', bitrate: number) {
  // FIX: WebRTC spec requires \r\n (CRLF) for all line breaks.
  const lines = sdp.split('\r\n');
  let line = -1;

  for (let i = 0; i < lines.length; i++) {
    if (lines[i].indexOf(`m=${media}`) === 0) {
      line = i;
      break;
    }
  }

  if (line === -1) {
    console.debug('Could not find the m line for', media);
    return sdp;
  }
  console.debug('Found the m line for', media, 'at line', line);

  // Pass the m line
  line++;

  // Skip i and c lines safely
  while (lines[line] && (lines[line].indexOf('i=') === 0 || lines[line].indexOf('c=') === 0)) {
    line++;
  }

  // If we're on a b line, replace it
  if (lines[line] && lines[line].indexOf('b') === 0) {
    console.debug('Replaced b line at line', line);
    lines[line] = `b=AS:${bitrate}`;
    return lines.join('\r\n'); // Rejoin with CRLF
  }

  // Add a new b line
  console.debug('Adding new b line before line', line);
  let newLines = lines.slice(0, line);
  newLines.push(`b=AS:${bitrate}`);
  newLines = newLines.concat(lines.slice(line, lines.length));

  return newLines.join('\r\n'); // Rejoin with CRLF
}

function preferCodec(sdp: string, codec: string, info: any) {
  let preferCodecNumber = '';

  if (codec === 'vp8') {
    if (!info.vp8LineNumber) return sdp;
    preferCodecNumber = info.vp8LineNumber;
  } else if (codec === 'vp9') {
    if (!info.vp9LineNumber) return sdp;
    preferCodecNumber = info.vp9LineNumber;
  } else if (codec === 'h264') {
    if (!info.h264LineNumber) return sdp;
    preferCodecNumber = info.h264LineNumber;
  }

  let newLine = `${info.videoCodecNumbersOriginal.split('SAVPF')[0]}SAVPF `;

  const newOrder = [preferCodecNumber];
  info.videoCodecNumbers.forEach(function (codecNumber: string) {
    if (codecNumber === preferCodecNumber) return;
    newOrder.push(codecNumber);
  });

  newLine += newOrder.join(' ');

  return sdp.replace(info.videoCodecNumbersOriginal, newLine);
}

function splitLines(sdp: string): any {
  const info: any = {};

  // FIX: WebRTC spec requires \r\n (CRLF)
  sdp.split('\r\n').forEach(function (line) {
    if (line.indexOf('m=video') === 0) {
      info.videoCodecNumbers = [];
      const parts = line.split('SAVPF');
      if (parts.length > 1) {
        parts[1].split(' ').forEach(function (codecNumber) {
          codecNumber = codecNumber.trim();
          if (!codecNumber || !codecNumber.length) return;
          info.videoCodecNumbers.push(codecNumber);
          info.videoCodecNumbersOriginal = line;
        });
      }
    }

    if (line.indexOf('VP8/90000') !== -1 && !info.vp8LineNumber) {
      info.vp8LineNumber = line.replace('a=rtpmap:', '').split(' ')[0];
    }

    if (line.indexOf('VP9/90000') !== -1 && !info.vp9LineNumber) {
      info.vp9LineNumber = line.replace('a=rtpmap:', '').split(' ')[0];
    }

    if (line.indexOf('H264/90000') !== -1 && !info.h264LineNumber) {
      info.h264LineNumber = line.replace('a=rtpmap:', '').split(' ')[0];
    }
  });

  return info;
}

function preferSelectedCodec(sdp: string, codec: 'vp8' | 'vp9' | 'h264' | string) {
  const info = splitLines(sdp);

  // ADD THIS LINE: Protect against malformed or missing video payloads
  if (!info || !info.videoCodecNumbers || info.videoCodecNumbers.length === 0) return sdp;

  if (codec === 'vp8' && info.vp8LineNumber === info.videoCodecNumbers[0]) return sdp;
  if (codec === 'vp9' && info.vp9LineNumber === info.videoCodecNumbers[0]) return sdp;
  if (codec === 'h264' && info.h264LineNumber === info.videoCodecNumbers[0]) return sdp;

  return preferCodec(sdp, codec, info);
}

export function morphSDP(sdp: string, options: P2PPeerOptions) {
  const { videoMaxBitrate, audioMaxBitrate, preferredCodecs } = options;

  if (options.type === ConferenceTypeEnum.video && videoMaxBitrate) {
    sdp = setMediaBitrate(sdp, 'video', videoMaxBitrate as number);
  }

  if (audioMaxBitrate) {
    sdp = setMediaBitrate(sdp, 'audio', audioMaxBitrate as number);
  }

  if (preferredCodecs && options.type === ConferenceTypeEnum.video) {
    // FIX: Iterate in reverse.
    // This ensures that the MOST preferred codec is moved to the front LAST, maintaining its priority.
    [...preferredCodecs].reverse().forEach((codec) => {
      sdp = preferSelectedCodec(sdp, codec);
    });
  }

  return sdp;
}

export async function getDeviceProfile(): Promise<DeviceProfile> {
  registerWebrtcPolyfills();

  let supportedVideoCodecs: string[] = [];
  let supportedAudioCodecs: string[] = [];

  // 1. Try the modern, synchronous API first
  try {
    if (typeof RTCRtpReceiver !== 'undefined' && RTCRtpReceiver.getCapabilities) {
      const videoCaps = RTCRtpReceiver.getCapabilities('video');
      if (videoCaps && videoCaps.codecs) {
        const vCodecs = videoCaps.codecs.map((c) => c.mimeType.split('/')[1].toLowerCase());
        supportedVideoCodecs = Array.from(new Set(vCodecs));
      }

      const audioCaps = RTCRtpReceiver.getCapabilities('audio');
      if (audioCaps && audioCaps.codecs) {
        const aCodecs = audioCaps.codecs.map((c) => c.mimeType.split('/')[1].toLowerCase());
        supportedAudioCodecs = Array.from(new Set(aCodecs));
      }
    }
  } catch (e) {
    console.warn('Could not read RTCRtpReceiver capabilities', e);
  }

  // 2. FALLBACK: If the modern API returned empty arrays (common in RN), use the Dummy SDP method
  if (supportedVideoCodecs.length === 0 || supportedAudioCodecs.length === 0) {
    try {
      const pc = new RTCPeerConnection();
      const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });

      if (offer.sdp) {
        const lines = offer.sdp.split('\r\n');
        lines.forEach((line) => {
          if (line.startsWith('a=rtpmap:')) {
            const match = line.match(/a=rtpmap:\d+ (.*?)\/\d+/);
            if (match && match[1]) {
              const codec = match[1].toLowerCase();
              if (['opus', 'isac', 'ilbc', 'pcmu', 'pcma', 'g722', 'telephone-event'].includes(codec)) {
                supportedAudioCodecs.push(codec);
              } else if (['vp8', 'vp9', 'h264', 'h265', 'av1'].includes(codec)) {
                supportedVideoCodecs.push(codec);
              }
            }
          }
        });
      }

      // Deduplicate
      supportedVideoCodecs = Array.from(new Set(supportedVideoCodecs));
      supportedAudioCodecs = Array.from(new Set(supportedAudioCodecs));

      pc.close(); // Clean up memory!
    } catch (e) {
      console.warn('Failed to parse codecs from dummy SDP', e);
    }
  }

  // 3. LAST RESORT: Safe hardcoded fallbacks if everything else completely failed
  if (supportedVideoCodecs.length === 0) supportedVideoCodecs = ['vp8', 'h264'];
  if (supportedAudioCodecs.length === 0) supportedAudioCodecs = ['opus', 'pcmu', 'pcma'];

  const osVersion = String(Platform.Version);
  const deviceInfo = {} as Partial<DeviceProfile>;

  // Map non-function properties from expo-device
  for (const key in Device) {
    if (typeof Device[key] !== 'function' && key !== 'DeviceType') {
      deviceInfo[key] = Device[key];
    }
  }

  if (Platform.OS === 'web') {
    deviceInfo.deviceMemory = String((navigator as any).deviceMemory || 'unknown');
    deviceInfo.userAgent = navigator.userAgent;
  }

  // Safely await the actual Device module function
  try {
    deviceInfo.deviceType = (await Device.getDeviceTypeAsync()) as any;
  } catch (e) {
    deviceInfo.deviceType = '0'; // Unknown fallback
  }

  deviceInfo.platform = PlatformType;
  deviceInfo.osVersion = osVersion;
  deviceInfo.supportedVideoCodecs = supportedVideoCodecs;
  deviceInfo.supportedAudioCodecs = supportedAudioCodecs;

  return deviceInfo as DeviceProfile;
}

function getPlatformType(): PlatformOSType | 'android-web' | 'ios-web' {
  let platformStr = Platform.OS as any;
  if (Platform.OS === 'web') {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('android')) platformStr = 'android-web';
    else if (userAgent.includes('iphone') || userAgent.includes('ipad')) platformStr = 'ios-web';
  }
  return platformStr;
}

export const PlatformType = getPlatformType();

export const isMobileDevice = ['android', 'ios', 'android-web', 'ios-web'].includes(PlatformType);

/**
 * MODERN API: Apply bandwidth dynamically mid-call WITHOUT needing to restart ICE or munge SDP.
 * Use this if a user clicks a "Low Data Mode" button during an active call.
 */
export async function applyBandwidthLimit(pc: RTCPeerConnection, limit: 125 | 250 | 500 | 1000 | 2000 | 'unlimited') {
  return Promise.all(
    pc.getSenders().map(async (sender) => {
      if (!sender.track || sender.track.kind !== 'video') return;

      const parameters = sender.getParameters();
      if (!parameters.encodings || !parameters.encodings.length) {
        parameters.encodings = [{}];
      }

      if (limit === 'unlimited') {
        delete parameters.encodings[0].maxBitrate;
      } else {
        parameters.encodings[0].maxBitrate = limit * 1000;
      }

      try {
        await sender.setParameters(parameters);
      } catch (err) {
        console.warn('Failed to apply dynamic bandwidth limit:', err);
      }
    }),
  );
}
